import { Review, WildberriesReviewsResponse } from "../types";

export const createPlaceholderProduct = (
  overrides: Partial<Review["product"]> = {}
): Review["product"] => {
  return {
    created_at: "",
    currency_code: "",
    id: "",
    images: [],
    is_archived: false,
    is_autoarchived: false,
    name: "",
    offer_id: "",
    price: 0,
    primary_image: [],
    sku: "",
    statuses: {
      is_created: false,
      moderate_status: "",
      status: "",
      status_description: "",
      status_failed: "",
      status_name: "",
      status_tooltip: "",
      status_updated_at: "",
      validation_status: "",
    },
    stocks: {
      has_stock: false,
      stocks: [],
    },
    updated_at: "",
    visibility_details: {
      has_price: false,
      has_stock: false,
    },
    ...overrides,
  };
};

export const mapOzonToShared = (ozon: unknown): Review => {
  const anyO = ozon as Record<string, unknown>;
  const productInfo = anyO?.product_info as
    | {
        id?: number;
        name?: string;
        offer_id?: string;
        images?: string[];
        primary_image?: string[];
      }
    | undefined;

  const productFromInfo = productInfo
    ? createPlaceholderProduct({
        id: String(productInfo.id ?? ""),
        name: productInfo.name ?? "",
        offer_id: productInfo.offer_id ?? "",
        images: productInfo.images ?? [],
        primary_image: productInfo.primary_image ?? [],
      })
    : undefined;

  const productFinal =
    (anyO as any).product || productFromInfo || createPlaceholderProduct();

  const sellerAnswerText =
    (anyO as any).answer !== undefined && (anyO as any).answer !== null
      ? (anyO as any).answer
      : ((anyO as any).sellerAnswer ?? null);
  const isAnswered =
    typeof sellerAnswerText === "string"
      ? sellerAnswerText.trim().length > 0
      : Boolean(sellerAnswerText);

  const mapped: Review = {
    comments_amount: (anyO as any).comments_amount ?? 0,
    id: String((anyO as any).id ?? ""),
    isAnalyzed: Boolean((anyO as any).isAnalyzed) ?? false,
    isAnsweredBySeller: (anyO as any).isAnsweredBySeller ?? isAnswered,
    isFallback: Boolean((anyO as any).isFallback) ?? false,
    isResponded: Boolean((anyO as any).isResponded) ?? false,
    is_rating_participant:
      Boolean((anyO as any).is_rating_participant) ?? false,
    offer_id: String((anyO as any).offer_id ?? ""),
    order_status: String((anyO as any).order_status ?? ""),
    photos_amount: Number((anyO as any).photos_amount ?? 0),
    platform: "ozon",
    problems: ((anyO as any).problems ?? null) as string | null,
    product: productFinal,
    published_at: ((anyO as any).published_at ||
      (anyO as any).publishedAt ||
      "") as string,
    rating: Number((anyO as any).rating ?? 0),
    recommendations: ((anyO as any).recommendations ?? null) as string | null,
    responseTemplate: ((anyO as any).responseTemplate ?? null) as string | null,
    sellerAnswer: (sellerAnswerText as string | null) ?? null,
    sentiment: ((anyO as any).sentiment ?? null) as string | null,
    sentimentAnalysis: ((anyO as any).sentimentAnalysis ?? null) as
      | string
      | null,
    sku: Number((anyO as any).sku ?? 0),
    status: String((anyO as any).status ?? ""),
    text: String((anyO as any).text ?? ""),
    videos_amount: Number((anyO as any).videos_amount ?? 0),
  };
  if (productInfo) (mapped as any).product_info = productInfo;
  return mapped;
};

export const mapWildberriesToShared = (
  wb: WildberriesReviewsResponse["data"]["feedbacks"][number]
): Review => {
  const textParts = [wb.text, wb.pros, wb.cons].filter(Boolean);
  const productName = wb.productDetails?.productName || "";
  const productOfferId =
    (wb.productDetails?.supplierArticle as any)?.toString?.() || "";
  const productId = (wb.productDetails?.nmId as any)?.toString?.() || "";
  return {
    editable:
      typeof wb.answer?.editable === "boolean" ? wb.answer.editable : undefined,
    comments_amount: 0,
    id: String(wb.id),
    isAnalyzed: (wb as any).isAnalyzed ?? false,
    isAnsweredBySeller: Boolean(wb.answer?.text),
    isFallback: (wb as any).isFallback ?? false,
    isResponded: (wb as any).isResponded ?? Boolean(wb.answer?.text),
    is_rating_participant: false,
    offer_id: productOfferId,
    order_status: wb.state || "",
    photos_amount: wb.photoLinks?.length || 0,
    platform: "wildberries",
    problems: (wb as any).problems ?? null,
    product: createPlaceholderProduct({
      id: productId,
      name: productName,
      offer_id: productOfferId,
    }),
    published_at: wb.createdDate,
    rating: wb.productValuation ?? 0,
    recommendations: (wb as any).recommendations ?? null,
    responseTemplate: (wb as any).responseTemplate ?? null,
    sellerAnswer: wb.answer?.text || null,
    sentiment: (wb as any).sentiment ?? null,
    sentimentAnalysis: (wb as any).sentimentAnalysis ?? null,
    sku: Number(wb.productDetails?.nmId) || 0,
    status: wb.state || "",
    text: textParts.join(" \n"),
    videos_amount: wb.video ? 1 : 0,
    userName: (wb as any).userName || undefined,
  } as Review;
};
